import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { NzModalRef, NzModalService, NzModalState } from 'ng-zorro-antd/modal';
import { of, Subscription, timer } from 'rxjs';
import { distinctUntilChanged, map, startWith, tap } from 'rxjs/operators';
import { LimitsService } from 'src/app/services/limits.service';

import { AutoLotView } from '../../../const';
import {
  LotTranslation,
  LotTypeEnum,
  UserLotQuery,
} from '../../graphql/service/graphql-auto-main-service';
import { LotSource } from '../../graphql/service/graphql-cars-default-service';
import { TranslationKey, TranslationState } from './types/translation.type';
import { LotTranslateRequestCreateModalComponent } from './components/lot-translate-request-create-modal/lot-translate-request-create-modal.component';
import { LotTranslationService } from './services/lot-translation.service';

@Component({
  selector: 'app-lot-create-translation-request-button',
  templateUrl: './lot-create-translation-request-button.component.html',
  styleUrls: ['./lot-create-translation-request-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LotCreateTranslationRequestButtonComponent implements OnInit, OnDestroy, OnChanges {
  @Input() currentUser: UserLotQuery['currentUser'];
  @Input() lot: AutoLotView = null;
  @Input() lotSource: LotSource;
  @Input() lotType: LotTypeEnum;
  @Input() aiTranslationEnabled = false;
  @Input() fullSize = false;
  @Input() translations: LotTranslation[] = [];
  @Output() clickIfTranslationsExists = new EventEmitter<void>();
  @Output() translationRequestCreated = new EventEmitter<void>();

  @HostBinding('style.display')
  display: 'none' | 'inline-block' = 'none';

  loading$ = of(false);

  private modalRef?: NzModalRef;

  private state: TranslationState = {
    has: {
      ruText: false,
      enText: false,
      ruAudio: false,
      enAudio: false,
      aiRu: false,
    },
    manualReady: false,
    aiReady: false,
    allReady: false,
    canRequestAiRu: false,
  };

  private timerSubscription: Subscription | null = null;

  constructor(
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private modalService: NzModalService,
    private limitService: LimitsService,
    private lotTranslationService: LotTranslationService,
  ) {}

  ngOnInit(): void {
    this.syncFromInputs();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['translations'] ||
      changes['aiTranslationEnabled'] ||
      changes['currentUser'] ||
      changes['lot']
    ) {
      this.syncFromInputs();
      this.cdr.markForCheck();
    }
  }

  private syncFromInputs(): void {
    this.state = this.buildTranslationState(false);
    this.updateView();
    this.syncTimer();
  }

  private updateView(): void {
    if (this.isHiddenByParameters) {
      this.display = 'none';
      this.closeModal();
      return;
    }

    if (this.isManager) {
      this.display = 'inline-block';
      return;
    }

    const hideByTime = this.oneHourTimeLimitReached || this.saturdayTimeLimitReached;

    if (hideByTime) {
      this.display = 'none';
      this.closeModal();
      return;
    }

    this.display = 'inline-block';
  }

  private syncTimer(): void {
    const should = this.shouldRunTimer();
    const running = !!this.timerSubscription;

    if (should && !running) {
      this.startTimer();
    }

    if (!should && running) {
      this.stopTimer();
    }
  }

  private shouldRunTimer(): boolean {
    return !this.isManager && !this.isHiddenByParameters;
  }

  private get isHiddenByParameters(): boolean {
    if (!this.lot || !this.currentUser) {
      return true;
    }

    return !!this.lot.isRestricted || this.lot.status === 'won';
  }

  private get isManager(): boolean {
    return !!this.currentUser && (this.currentUser.isAuctioneer || this.currentUser.isTranslator);
  }

  private startTimer(): void {
    this.timerSubscription = timer(0, 2000)
      .pipe(
        map(() => this.oneHourTimeLimitReached || this.saturdayTimeLimitReached),
        distinctUntilChanged(),
      )
      .subscribe((hide) => {
        this.display = hide ? 'none' : 'inline-block';
        if (hide) {
          this.closeModal();
        }
        this.cdr.markForCheck();
      });
  }

  private stopTimer(): void {
    this.timerSubscription?.unsubscribe();
    this.timerSubscription = null;
  }

  openCreateUserRequestModal(): void {
    if (!this.shouldCheckAi) {
      this.handleState(this.state);
      return;
    }

    this.loading$ = this.lotTranslationService.getAiEnabled().pipe(
      tap((aiEnabled) => {
        const state = this.buildTranslationState(aiEnabled);
        this.handleState(state);
      }),
      map(() => false),
      startWith(true),
    );
  }

  private handleState(state: TranslationState): void {
    if (
      this.isHiddenByParameters ||
      (!this.isManager && (this.oneHourTimeLimitReached || this.saturdayTimeLimitReached))
    ) {
      return;
    }

    const aiFlowEnabled = state.canRequestAiRu || state.aiReady;
    const fullyReady = aiFlowEnabled ? state.manualReady && state.aiReady : state.manualReady;

    if (fullyReady) {
      this.clickIfTranslationsExists.emit();
      return;
    }

    this.modalRef = this.modalService.create({
      nzTitle: this.translate.instant('USER_REQUESTS.LOT_TRANSLATE_REQUEST'),
      nzContent: LotTranslateRequestCreateModalComponent,
      nzComponentParams: {
        lotId: this.lot.id,
        lotType: this.lotType,
        lotSource: this.lotSource,
        currentUser: this.currentUser,
        translationRequestCreated: this.translationRequestCreated,
        translationState: state,
      },
    });
  }

  private buildTranslationState(aiEnabled: boolean): TranslationState {
    const translations = this.translations ?? [];

    const isAi = (t: LotTranslation) => !!t.aiModel || !!t.aiProvider;
    const isManual = (t: LotTranslation) => !isAi(t) && !!t.translatorId;

    const hasRuText = translations.some(
      (t) => isManual(t) && t.language === 'ru' && !!t.message?.trim(),
    );

    const hasEnText = translations.some(
      (t) => isManual(t) && t.language === 'en' && !!t.message?.trim(),
    );

    const hasRuAudio = translations.some((t) => isManual(t) && t.language === 'ru' && !!t.file);

    const hasAiRu = translations.some((t) => isAi(t) && t.language === 'ru');

    const canRequestAiRu = this.canUseAi(hasAiRu) && aiEnabled;

    const has: Record<TranslationKey, boolean> = {
      ruText: hasRuText,
      enText: hasEnText,
      ruAudio: hasRuAudio,
      enAudio: false,
      aiRu: hasAiRu,
    };

    const manualReady = hasRuText && hasEnText && hasRuAudio;
    const aiReady = hasAiRu;
    const allReady = manualReady && aiReady;

    return { has, manualReady, aiReady, allReady, canRequestAiRu };
  }

  private get shouldCheckAi(): boolean {
    return this.canUseAi(this.state.aiReady);
  }

  private canUseAi(hasAiRu: boolean): boolean {
    const imageCount = this.lot?.images?.length ?? 0;
    const isRussianUser = (this.currentUser?.countryIso ?? '').toLowerCase() === 'ru';

    return isRussianUser && imageCount > 1 && !hasAiRu && this.aiTranslationEnabled;
  }

  private get oneHourTimeLimitReached(): boolean {
    return this.limitService.checkOneHourBeforeAuctionReached(this.lot?.time);
  }

  private get saturdayTimeLimitReached(): boolean {
    const nowJp = DateTime.now().setZone('Asia/Tokyo');
    const auctionDate = DateTime.fromISO(this.lot.date, { zone: 'Asia/Tokyo' });

    return (
      auctionDate.startOf('day').equals(nowJp.startOf('day')) &&
      nowJp.weekday === 6 &&
      nowJp.hour >= 11
    );
  }

  private closeModal(): void {
    if (this.modalRef?.state === NzModalState.OPEN) {
      this.modalRef.destroy();
    }
  }

  ngOnDestroy(): void {
    this.stopTimer();
  }
}
